import React, { useEffect, useMemo, useState } from 'react';
import { Link2, Plus, Save, Trash2 } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import Toast, { ToastMessage } from '../../common/Toast';
import { CapaRootCause, capaRootCausesService, CauseTaskLink } from '../../../services/capaRootCausesService';
import { tasksService, Task, TaskStatus } from '../../../services/tasksService';
import { User } from '../../../services/userService';
import RCAIdentifierPicker from './RCAIdentifierPicker';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../../validation/ValidatedFields';
import { confirmDelete, confirmWarning } from '../../common/confirm';
import extractApiError from '../../../utils/apiError';

// ── Public contract ──────────────────────────────────────────────────────────

export type Step = 'reason' | 'identify' | 'remediate';

export interface CauseAnalysisModalProps {
    isOpen: boolean;
    investigationId: number;
    cause: CapaRootCause | null;     // the ROOT cause being analyzed
    allCauses: CapaRootCause[];      // all causes for the impact (to build the why-chain)
    users: User[];                   // for remedial doer/validator/approver selects
    readOnly: boolean;               // frozen: hide all mutating surfaces
    onClose: () => void;
    onChanged: () => void;           // parent reloads causes + actions summary
}

// ── Constants ────────────────────────────────────────────────────────────────

const MAX_WHYS = 5;

// ── Helpers ──────────────────────────────────────────────────────────────────

const userLabel = (u: User) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    if (name && u.login_id) return `${name} (${u.login_id})`;
    return name || u.login_id || `#${u.id}`;
};

const taskStatusVariant = (s: TaskStatus): string => {
    switch (s) {
        case 'DONE':
        case 'VERIFIED':    return 'success';
        case 'IN_PROGRESS': return 'info';
        case 'REVERTED':
        case 'REJECTED':
        case 'CANCELLED':   return 'danger';
        case 'ARCHIVED':    return 'neutral';
        default:            return 'warning'; // OPEN
    }
};

const ACTIVE_STATUSES: ReadonlyArray<string> = ['OPEN', 'IN_PROGRESS', 'REVERTED'];

// ── Component ────────────────────────────────────────────────────────────────

const CauseAnalysisModal: React.FC<CauseAnalysisModalProps> = ({
    isOpen,
    investigationId,
    cause,
    allCauses,
    users,
    readOnly,
    onClose,
    onChanged,
}) => {
    const [step, setStep] = useState<Step>('reason');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    // Reason Further state
    const [addingWhy, setAddingWhy] = useState(false);
    const [whyText, setWhyText] = useState('');
    const fieldVh = useFieldValidation();

    // Remediate step state
    const [linkedTasks, setLinkedTasks] = useState<Task[]>([]);
    const [allCapaTasks, setAllCapaTasks] = useState<Task[]>([]);
    const [remedialMode, setRemedialMode] = useState<'create' | 'link'>('create');
    const [createForm, setCreateForm] = useState<CauseTaskLink>({ name: '' });
    const [linkTaskId, setLinkTaskId] = useState<number | null>(null);

    // Reset to reason step whenever the selected cause changes
    useEffect(() => {
        setStep('reason');
        setAddingWhy(false);
        setWhyText('');
        fieldVh.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cause?.id]);

    // Load action tasks when entering Remediate step
    useEffect(() => {
        if (step !== 'remediate' || !cause) return;
        let cancelled = false;
        (async () => {
            try {
                const [allCapa, causeTasks] = await Promise.all([
                    tasksService.list({ source_type_code: 'CAPA_ISSUE', limit: 500 }),
                    capaRootCausesService.listTasks(cause.id),
                ]);
                if (!cancelled) {
                    setAllCapaTasks(allCapa);
                    setLinkedTasks(causeTasks);
                }
            } catch (e: any) {
                if (!cancelled) {
                    setToast({ type: 'error', message: extractApiError(e, 'Failed to load remedial actions') });
                    setAllCapaTasks([]);
                    setLinkedTasks([]);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [cause?.id, step]);

    // Reset remedial form when entering remediate step
    useEffect(() => {
        if (step === 'remediate') {
            setRemedialMode('create');
            setCreateForm({ name: '' });
            setLinkTaskId(null);
            fieldVh.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // Build parent → children map from allCauses
    const byParent = useMemo(() => {
        const m = new Map<number | null, CapaRootCause[]>();
        for (const c of allCauses) {
            const k = c.prev_record_id ?? null;
            if (!m.has(k)) m.set(k, []);
            m.get(k)!.push(c);
        }
        return m;
    }, [allCauses]);

    // Walk first-child only to get the linear why-chain below cause
    const whyChain = useMemo<CapaRootCause[]>(() => {
        if (!cause) return [];
        const chain: CapaRootCause[] = [];
        let cursor = cause.id;
        // A 5-Why chain is a single thread capped at MAX_WHYS; this also guards
        // against any pathological cycle in prev_record_id.
        while (chain.length < MAX_WHYS) {
            const kids = byParent.get(cursor) ?? [];
            if (kids.length === 0) break;
            chain.push(kids[0]);
            cursor = kids[0].id;
        }
        return chain;
    }, [cause, byParent]);

    const whyTip: CapaRootCause | null =
        whyChain.length > 0 ? whyChain[whyChain.length - 1] : cause;

    // Computed link options for the SearchSelect (active tasks only, already-linked disabled)
    const linkOptions = useMemo(() => {
        const linkedIds = new Set(linkedTasks.map(t => t.id));
        return allCapaTasks
            .filter(t => ACTIVE_STATUSES.includes(t.status))
            .map(t => ({
                value: t.id,
                label: `${t.name} [${t.status}]${linkedIds.has(t.id) ? ' — already linked' : ''}`,
                disabled: linkedIds.has(t.id),
            }));
    }, [allCapaTasks, linkedTasks]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const submitWhy = async () => {
        if (!cause || readOnly) return;
        const ok = fieldVh.validateAll([{ name: 'why', code: 110, value: whyText, required: true }]);
        if (!ok) return;
        setSubmitting(true);
        try {
            await capaRootCausesService.create(investigationId, {
                bucket: cause.bucket,
                cause_description: whyText,
                impact_id: cause.impact_id ?? 0,
                prev_record_id: whyTip ? whyTip.id : cause.id,
            });
            setWhyText(''); setAddingWhy(false); fieldVh.reset();
            setToast({ type: 'success', message: 'Why added.' });
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to add why') });
        } finally { setSubmitting(false); }
    };

    const saveIdentify = async (tid: number | null, rid: number | null) => {
        if (!whyTip || readOnly) return;
        setSubmitting(true);
        try {
            await capaRootCausesService.update(investigationId, whyTip.id, { item_type_id: tid, item_record_id: rid });
            setToast({ type: 'success', message: 'Identification saved.' });
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save identification') });
        } finally { setSubmitting(false); }
    };

    const handleDeleteAll = async () => {
        if (!cause || readOnly) return;
        const ok = await confirmDelete({ text: 'Delete this entire root cause and all its 5-Why reasoning? This cannot be undone.' });
        if (!ok) return;
        setSubmitting(true);
        try {
            // deepest why first so no node ever has a child at delete time
            for (let i = whyChain.length - 1; i >= 0; i--) {
                await capaRootCausesService.remove(investigationId, whyChain[i].id);
            }
            await capaRootCausesService.remove(investigationId, cause.id);
            setToast({ type: 'delete', message: 'Root cause deleted.' });
            onChanged();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to delete') });
        } finally { setSubmitting(false); }
    };

    const reloadRemedialLists = async () => {
        if (!cause) return;
        try {
            const [allCapa, causeTasks] = await Promise.all([
                tasksService.list({ source_type_code: 'CAPA_ISSUE', limit: 500 }),
                capaRootCausesService.listTasks(cause.id),
            ]);
            setAllCapaTasks(allCapa);
            setLinkedTasks(causeTasks);
        } catch (e) { /* non-fatal: the mutation already succeeded */ console.warn('Failed to reload remedial lists', e); }
    };

    const handleSubmitRemedial = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!cause) return;
        try {
            setSubmitting(true);
            if (remedialMode === 'link') {
                if (!linkTaskId) {
                    setToast({ type: 'error', message: 'Pick a task to link' });
                    return;
                }
                await capaRootCausesService.linkOrCreateTask(cause.id, { task_id: linkTaskId });
                setToast({ type: 'success', message: 'Linked.' });
            } else {
                const valid = fieldVh.validateAll([
                    { name: 'name', code: 109, value: createForm.name, required: true },
                    { name: 'description', code: 110, value: createForm.description },
                    { name: 'due_date', code: 305, value: createForm.due_date },
                ]);
                if (!valid) {
                    setSubmitting(false);
                    return;
                }
                await capaRootCausesService.linkOrCreateTask(cause.id, createForm);
                setToast({ type: 'success', message: 'Remedial action created.' });
            }
            // Reset form
            setCreateForm({ name: '' });
            setLinkTaskId(null);
            fieldVh.reset();
            // Reload both lists
            await reloadRemedialLists();
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save remedial action') });
        } finally { setSubmitting(false); }
    };

    const handleUnlink = async (task: Task) => {
        if (!cause || readOnly) return;
        const ok = await confirmWarning({
            title: 'Unlink this action from this cause?',
            text: 'The task itself remains, but it will no longer appear under this cause.',
            confirmText: 'Yes, Unlink',
        });
        if (!ok) return;
        try {
            await capaRootCausesService.unlinkTask(cause.id, task.id);
            setToast({ type: 'success', message: 'Unlinked.' });
            await reloadRemedialLists();
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to unlink') });
        }
    };

    // ── Render helpers ───────────────────────────────────────────────────────

    const renderReasonStep = () => (
        <div data-testid="GRP_cause_reason_step">
            {/* Root cause row */}
            {cause && (
                <div className="capa-why-node" style={{ marginBottom: 8 }}>
                    <div className="capa-why-node-row">
                        <span className="status-badge sm neutral">Root cause</span>
                        <span className="capa-why-node-text">{cause.cause_description}</span>
                        {cause.linked_item_label && (
                            <span className="status-badge sm neutral">{cause.linked_item_label}</span>
                        )}
                    </div>
                </div>
            )}

            {/* Why-chain ladder */}
            {whyChain.length === 0 ? (
                <div
                    style={{
                        padding: '12px 8px',
                        color: '#9ca3af',
                        fontSize: '0.8rem',
                        fontStyle: 'italic',
                    }}
                    data-testid="MSG_cause_no_whys"
                >
                    No deeper whys yet.
                </div>
            ) : (
                <div data-testid="GRP_cause_why_ladder">
                    {whyChain.map((c, i) => (
                        <div key={c.id} className="capa-why-node">
                            <div className="capa-why-node-row">
                                <span className="status-badge sm neutral">Why {i + 1}</span>
                                <span className="capa-why-node-text">{c.cause_description}</span>
                                {c.linked_item_label && (
                                    <span className="status-badge sm neutral">{c.linked_item_label}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Inline add-why form */}
            {step === 'reason' && !readOnly && whyChain.length < MAX_WHYS && (
                <div style={{ marginTop: 12 }}>
                    {!addingWhy ? (
                        <Button
                            variant="outline"
                            onClick={() => setAddingWhy(true)}
                            data-testid="BTN_cause_reason_further"
                        >
                            Reason Further
                        </Button>
                    ) : (
                        <div data-testid="GRP_cause_add_why_form" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <ValidatedTextarea
                                vh={fieldVh}
                                name="why"
                                code={110}
                                required={true}
                                label={`Why ${whyChain.length + 1} of ${MAX_WHYS}`}
                                value={whyText}
                                onChange={e => { setWhyText(e.target.value); fieldVh.clearError('why'); }}
                                placeholder="What is the deeper why?"
                                rows={3}
                                data-testid="INP_cause_why_text"
                            />
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button
                                    variant="fill"
                                    onClick={submitWhy}
                                    disabled={submitting}
                                    data-testid="BTN_cause_why_confirm"
                                >
                                    {submitting ? 'Adding...' : 'Add Why'}
                                </Button>
                                <Button
                                    variant="secondary-outline"
                                    onClick={() => { setAddingWhy(false); setWhyText(''); fieldVh.reset(); }}
                                    data-testid="BTN_cause_why_cancel"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderIdentifyStep = () => (
        <div data-testid="GRP_cause_identify_step">
            {whyTip && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Ultimate cause
                    </div>
                    <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>
                        {whyTip.cause_description}
                    </div>
                </div>
            )}
            {!readOnly && whyTip ? (
                <RCAIdentifierPicker
                    bucket={whyTip.bucket}
                    itemTypeId={whyTip.item_type_id ?? null}
                    itemRecordId={whyTip.item_record_id ?? null}
                    onChange={saveIdentify}
                />
            ) : (
                whyTip?.linked_item_label ? (
                    <div style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd', fontSize: '0.875rem', color: '#0369a1' }}>
                        {whyTip.linked_item_label}
                    </div>
                ) : (
                    <div style={{ padding: '12px 8px', color: '#9ca3af', fontSize: '0.8rem', fontStyle: 'italic' }}>
                        No identifier linked yet.
                    </div>
                )
            )}
        </div>
    );

    const renderRemediateStep = () => (
        <div data-testid="GRP_cause_remediate_step">
            {/* Linked actions list */}
            <div style={{ marginBottom: 16 }}>
                {linkedTasks.length === 0 ? (
                    <>
                        <div
                            style={{ padding: '12px 8px', color: '#9ca3af', fontSize: '0.8rem', fontStyle: 'italic' }}
                            data-testid="MSG_cause_no_remedial"
                        >
                            No remedial actions linked to this cause yet.
                        </div>
                        {/* Incomplete signal */}
                        <div
                            style={{ padding: '8px 12px', background: '#fffbeb', borderRadius: 6, border: '1px solid #fde68a', fontSize: '0.8rem', color: '#92400e', marginTop: 4 }}
                            data-testid="MSG_cause_remedial_required"
                        >
                            A remedial action is required to complete this analysis.
                        </div>
                    </>
                ) : (
                    <div data-testid="GRP_cause_remedial_list">
                        {linkedTasks.map(t => (
                            <div key={t.id} className="capa-action-row">
                                <span className="capa-action-row-text">{t.name}</span>
                                <span className={`status-badge sm ${taskStatusVariant(t.status)}`}>
                                    {t.status.replace('_', ' ')}
                                </span>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        onClick={() => handleUnlink(t)}
                                        className="btn-danger-outline-none"
                                        title="Unlink"
                                        data-testid={`BTN_cause_remedial_row_${t.id}_unlink`}
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add-New / Link-Existing tabs — hidden in read-only mode */}
            {!readOnly && (
                <>
                    <div className="app-modal-tabs">
                        <button
                            type="button"
                            className={`app-modal-tab ${remedialMode === 'create' ? 'app-modal-tab--active' : ''}`}
                            onClick={() => setRemedialMode('create')}
                            data-testid="TAB_cause_remedial_create"
                        >
                            <Plus size={12} /> Add New
                        </button>
                        <button
                            type="button"
                            className={`app-modal-tab ${remedialMode === 'link' ? 'app-modal-tab--active' : ''}`}
                            onClick={() => setRemedialMode('link')}
                            data-testid="TAB_cause_remedial_link"
                        >
                            <Link2 size={12} /> Link Existing
                        </button>
                    </div>

                    <form
                        id="cause-remedial-form"
                        onSubmit={handleSubmitRemedial}
                        className="grid grid-cols-2 gap-x-6 gap-y-4"
                        data-testid="MOD_cause_remedial_form"
                    >
                        {remedialMode === 'create' && (
                            <>
                                <div className="col-span-2">
                                    <ValidatedInput
                                        vh={fieldVh}
                                        name="name"
                                        code={109}
                                        required={true}
                                        label="Name"
                                        value={createForm.name || ''}
                                        onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                                        placeholder="Short title"
                                        data-testid="INP_cause_remedial_name"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <ValidatedTextarea
                                        vh={fieldVh}
                                        name="description"
                                        code={110}
                                        label="Description"
                                        value={createForm.description || ''}
                                        onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                                        data-testid="INP_cause_remedial_description"
                                    />
                                </div>
                                <FormControl.SearchSelect
                                    label="Doer"
                                    options={users.map(u => ({ value: u.id, label: userLabel(u) }))}
                                    value={createForm.doer_id ?? null}
                                    onChange={v => setCreateForm(f => ({ ...f, doer_id: v ? Number(v) : null }))}
                                    placeholder="(default: you)"
                                    clearable
                                    data-testid="SEL_cause_remedial_doer"
                                />
                                <FormControl.SearchSelect
                                    label="Validator"
                                    options={users.map(u => ({ value: u.id, label: userLabel(u) }))}
                                    value={createForm.validator_id ?? null}
                                    onChange={v => setCreateForm(f => ({ ...f, validator_id: v ? Number(v) : null }))}
                                    clearable
                                    data-testid="SEL_cause_remedial_validator"
                                />
                                <FormControl.SearchSelect
                                    label="Approver"
                                    options={users.map(u => ({ value: u.id, label: userLabel(u) }))}
                                    value={createForm.approver_id ?? null}
                                    onChange={v => setCreateForm(f => ({ ...f, approver_id: v ? Number(v) : null }))}
                                    clearable
                                    data-testid="SEL_cause_remedial_approver"
                                />
                                <ValidatedDatePicker
                                    vh={fieldVh}
                                    name="due_date"
                                    code={305}
                                    label="Due Date"
                                    value={createForm.due_date || ''}
                                    onChange={e => setCreateForm(f => ({ ...f, due_date: e.target.value }))}
                                    data-testid="INP_cause_remedial_due_date"
                                />
                            </>
                        )}
                        {remedialMode === 'link' && (
                            <div className="col-span-2">
                                <FormControl.SearchSelect
                                    label="Existing CAPA Task"
                                    required
                                    options={linkOptions}
                                    value={linkTaskId}
                                    onChange={v => setLinkTaskId(v ? Number(v) : null)}
                                    placeholder={linkOptions.length === 0 ? '(no CAPA tasks exist yet)' : '— pick a task —'}
                                    clearable
                                    data-testid="SEL_cause_remedial_link_task"
                                />
                            </div>
                        )}
                    </form>
                </>
            )}
        </div>
    );

    // ── Footer actions per step ──────────────────────────────────────────────

    const renderStepActions = () => {
        if (step === 'reason') {
            return (
                <Button
                    variant="fill"
                    onClick={() => setStep('identify')}
                    data-testid="BTN_cause_next_identify"
                >
                    Next: Identify →
                </Button>
            );
        }
        if (step === 'identify') {
            return (
                <Button
                    variant="fill"
                    onClick={() => setStep('remediate')}
                    data-testid="BTN_cause_next_remediate"
                >
                    Next: Remediate →
                </Button>
            );
        }
        // Remediate step: show Submit button
        if (step === 'remediate' && !readOnly) {
            return (
                <Button
                    type="submit"
                    form="cause-remedial-form"
                    variant="fill"
                    icon={remedialMode === 'link' ? Link2 : Save}
                    disabled={submitting || (remedialMode === 'link' && !linkTaskId)}
                    data-testid="BTN_cause_remedial_submit"
                >
                    {submitting ? 'Submitting...' : 'Submit'}
                </Button>
            );
        }
        return null;
    };

    // ── Main render ──────────────────────────────────────────────────────────

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <Modal.Header>
                    RCA — {cause?.cause_description}
                </Modal.Header>

                <Modal.Body>
                    {/* 3-node stepper */}
                    <div className="capa-rca-stepper" data-testid="GRP_cause_stepper">
                        {(['reason', 'identify', 'remediate'] as Step[]).map((s, i) => (
                            <button
                                key={s}
                                type="button"
                                className={`capa-rca-step${step === s ? ' capa-rca-step--active' : ''}`}
                                onClick={() => setStep(s)}
                                data-testid={`BTN_cause_step_${s}`}
                            >
                                <span className="capa-rca-step-num">{i + 1}</span>
                                <span className="capa-rca-step-label">
                                    {s === 'reason' ? 'Reason' : s === 'identify' ? 'Identify' : 'Remediate'}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Step body */}
                    {step === 'reason' && renderReasonStep()}
                    {step === 'identify' && renderIdentifyStep()}
                    {step === 'remediate' && renderRemediateStep()}
                </Modal.Body>

                <Modal.Footer>
                    {!readOnly && cause && (
                        <Button
                            variant="delete-outline"
                            icon={Trash2}
                            onClick={handleDeleteAll}
                            disabled={submitting}
                            style={{ marginRight: 'auto' }}
                            data-testid="BTN_cause_delete_all"
                        >
                            Delete All
                        </Button>
                    )}
                    <Button variant="secondary-outline" onClick={onClose}>
                        Close
                    </Button>
                    {renderStepActions()}
                </Modal.Footer>

                <Toast toast={toast} onClose={() => setToast(null)} />
            </Modal>
        </>
    );
};

export default CauseAnalysisModal;
